import type { DraftStatus, DraftTrigger } from '../../../shared/enums';
import type { DraftWithLead, EmailDraft, Template } from '../../../shared/types';
import { todayLocal } from '../logic/dates';
import { apiFetch } from './api';
import { countByStatus, listOutbox, enqueue } from './outbox';
import { joinSync, syncAndSettle } from './sync';

/** Drafts/templates API client (M3, D33). Online-backed like the Route tab — generation and
 *  rendering live on the Worker so the copy is identical everywhere; only reading needs the app. */

/** Fired on `window` when new drafts exist, so an open screen (the Home's pending count) can refresh. */
export const DRAFTS_CHANGED = 'sb:drafts-changed';

/** Idempotent contract-draft generation on app open (D32). Queued writes go first (D45): a contract
 *  date entered offline must reach the server before it evaluates the 60/30-day windows; with nothing
 *  queued, the run already in flight at app open is all there is to wait for. */
export const generateDrafts = async (): Promise<number> => {
  if ((await countByStatus()).pending > 0) await syncAndSettle();
  else await joinSync();
  const data = await apiFetch<{ created: number }>('POST', '/api/drafts/generate', { today: todayLocal() });
  if (data.created > 0) window.dispatchEvent(new Event(DRAFTS_CHANGED));
  return data.created;
};

export const listDrafts = (
  filter: { status?: DraftStatus; lead_id?: string } = {},
): Promise<DraftWithLead[]> => {
  const params = new URLSearchParams();
  if (filter.status) params.set('status', filter.status);
  if (filter.lead_id) params.set('lead_id', filter.lead_id);
  const qs = params.toString();
  return apiFetch<DraftWithLead[]>('GET', `/api/drafts${qs ? `?${qs}` : ''}`);
};

/** A draft copied, dismissed or restored changes what is left to send: the counts listen (D65). */
export const setDraftStatus = async (id: string, status: DraftStatus): Promise<EmailDraft> => {
  const draft = await apiFetch<EmailDraft>('PATCH', `/api/drafts/${encodeURIComponent(id)}`, { status });
  window.dispatchEvent(new Event(DRAFTS_CHANGED));
  return draft;
};

export type ManualTrigger = Extract<DraftTrigger, 'post_visit' | 'meeting_confirm' | 'contract_expiring'>;

const draftsInFlight = new Map<string, Promise<EmailDraft>>();

/** Manual draft creation. Contract drafts send the Pacific date (D5 amended 2026-10-26) for milestone math.
 *  Queued writes go first: the lead (or the edit the draft should reflect) may exist only on the
 *  phone yet — e.g. the "draft the follow-up" toast right after a quick-log for a new lead.
 *  A repeat call for the same lead + trigger while one is in flight joins it: manual drafts are
 *  not deduplicated by the server, and the sync's re-render re-enables the buttons meanwhile. */
export const createDraft = (lead_id: string, trigger: ManualTrigger): Promise<EmailDraft> => {
  const key = `${lead_id}:${trigger}`;
  const pending = draftsInFlight.get(key);
  if (pending) return pending;
  const request = (async () => {
    await syncAndSettle();
    const ops = await listOutbox();
    if (ops.some(o => o.local_lead_id === lead_id)) {
      if (!ops.some(o => o.path === '/api/drafts' && (o.body as { lead_id?: string; trigger?: string })?.lead_id === lead_id && (o.body as { trigger?: string }).trigger === trigger))
        await enqueue({ method: 'POST', path: '/api/drafts', body: { lead_id, trigger, ...(trigger === 'contract_expiring' ? { today: todayLocal() } : {}) }, label: 'Prepare draft after lead recovery' });
      throw new Error('Draft saved in queue; resolve the lead in Sync first');
    }
    return apiFetch<EmailDraft>('POST', '/api/drafts', {
      lead_id,
      trigger,
      ...(trigger === 'contract_expiring' ? { today: todayLocal() } : {}),
    });
  })().finally(() => draftsInFlight.delete(key));
  draftsInFlight.set(key, request);
  return request;
};

export const listTemplates = (): Promise<Template[]> => apiFetch<Template[]>('GET', '/api/templates');

export const updateTemplate = (id: string, patch: { subject?: string; body?: string }): Promise<Template> =>
  apiFetch<Template>('PATCH', `/api/templates/${encodeURIComponent(id)}`, patch);

/** Clipboard with a fallback for older WebViews. Returns false when both paths fail. */
export const copyText = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
};
